/**
 * QuestionCard - Displays a single question with animation
 */

import { motion } from 'framer-motion'
import type { Question } from '@/types'
import { QuestionInput } from './QuestionInput'
import * as LucideIcons from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface QuestionCardProps {
  question: Question
  value: unknown
  onChange: (value: unknown) => void
  error?: string
  questionNumber: number
  totalQuestions: number
}

export function QuestionCard({
  question,
  value,
  onChange,
  error,
  questionNumber,
  totalQuestions,
}: QuestionCardProps) {
  // Get icon component if specified
  const IconComponent = question.icon 
    ? (LucideIcons as unknown as Record<string, LucideIcon>)[question.icon] 
    : null

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-2xl mx-auto"
    >
      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-6 text-muted-foreground">
        <span className="text-sm">
          Question {questionNumber} of {totalQuestions}
        </span>
        <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${(questionNumber / totalQuestions) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Question header */}
      <div className="mb-8">
        <div className="flex items-start gap-4">
          {IconComponent && (
            <div className="p-3 rounded-lg bg-primary/10">
              <IconComponent className="h-6 w-6 text-primary" />
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-2xl font-semibold leading-tight mb-2">
              {question.question}
            </h2>
            {question.description && (
              <p className="text-muted-foreground text-lg">
                {question.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Question input */}
      <div className="mb-6">
        <QuestionInput
          question={question}
          value={value}
          onChange={onChange}
          error={error}
        />
      </div>
    </motion.div>
  )
}
